/**
 * Demo request page — schools can request a personalized demo.
 * Creates a Lead (with DEMO_REQUEST source) and, in Phase 2, a Deal
 * at the "Demo gepland" stage.
 *
 * Lives outside `_auth/` and `_app/` layout groups.
 */

import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { data, Link, Form, useActionData } from 'react-router'
import { z } from 'zod'
import logoUrl from '#app/assets/favicons/favicon.svg'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { processLeadSignup } from '#app/utils/lead.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { type Route } from './+types/demo.ts'

const DemoRequestSchema = z.object({
	email: z
		.string({ required_error: 'Vul een e-mailadres in.' })
		.email('Vul een geldig e-mailadres in.'),
	name: z
		.string({ required_error: 'Vul je naam in.' })
		.min(2, 'Naam moet minimaal 2 tekens zijn.'),
	schoolName: z.string().optional(),
	message: z.string().optional(),
})

export const meta: Route.MetaFunction = () => [
	{ title: 'Demo aanvragen — Muziekdocent AI' },
	{
		name: 'description',
		content:
			'Plan een persoonlijke demo en ontdek hoe Muziekdocent AI in jouw lessen past.',
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, { schema: DemoRequestSchema })

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply(), success: false },
			{ status: 400 },
		)
	}

	const { email, name, schoolName } = submission.value

	await processLeadSignup({
		email,
		name,
		schoolName: schoolName || null,
		source: 'DEMO_REQUEST',
	})

	// TODO Phase 2: create Deal at "Demo gepland" stage
	// TODO Phase 2: send notification to founders

	return data({
		result: submission.reply({ resetForm: true }),
		success: true,
	})
}

export default function DemoRequestPage() {
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()
	const [form, fields] = useForm({
		id: 'demo-request-form',
		constraint: getZodConstraint(DemoRequestSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: DemoRequestSchema })
		},
		// 'onInput' per ADR-101: blur-time revalidation shifts the submit
		// button mid-click and can lose the click.
		shouldRevalidate: 'onInput',
	})

	const isSuccess = actionData?.success === true

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-[#18181b] px-6 py-16 text-[#e4e4e7]">
			<Link to="/" className="mb-12 flex items-center gap-2">
				<img src={logoUrl} alt="" className="h-8 w-8" />
				<span className="font-display text-[10px] text-neon-pink">
					MUZIEKDOCENT
				</span>
			</Link>

			<div className="w-full max-w-md rounded-2xl border border-white/5 bg-black/20 p-8">
				<h1 className="text-2xl font-bold text-white">
					Demo aanvragen
				</h1>
				<p className="mt-2 text-sm text-text-muted">
					We laten je in 20 minuten zien hoe Muziekdocent AI in jouw
					lessen past. Online, op een moment dat jou uitkomt.
				</p>

				{isSuccess ? (
					<div className="mt-8 rounded-xl border border-neon-green/20 bg-neon-green/5 p-6 text-center">
						<p className="text-sm font-medium text-neon-green">
							Aanvraag ontvangen
						</p>
						<p className="mt-2 text-sm text-text-muted">
							We nemen binnen 1 werkdag contact op om een moment te
							prikken.
						</p>
					</div>
				) : (
					<Form method="POST" {...getFormProps(form)} className="mt-8">
						<div className="space-y-4">
							<Field
								labelProps={{ children: 'Naam' }}
								inputProps={{
									...getInputProps(fields.name, { type: 'text' }),
									placeholder: 'Jan de Vries',
									autoFocus: true,
								}}
								errors={fields.name.errors}
							/>
							<Field
								labelProps={{ children: 'E-mail' }}
								inputProps={{
									...getInputProps(fields.email, { type: 'email' }),
									placeholder: 'j.devries@school.nl',
								}}
								errors={fields.email.errors}
							/>
							<Field
								labelProps={{ children: 'School (optioneel)' }}
								inputProps={{
									...getInputProps(fields.schoolName, {
										type: 'text',
									}),
									placeholder: 'CSG De Heemgaard',
								}}
								errors={fields.schoolName.errors}
							/>
							<div>
								<label
									htmlFor={fields.message.id}
									className="text-sm font-medium text-white"
								>
									Bericht (optioneel)
								</label>
								<textarea
									id={fields.message.id}
									name={fields.message.name}
									placeholder="Vertel kort wat je wilt zien of welke vragen je hebt."
									rows={3}
									className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-text-dim focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
								/>
								<ErrorList
									id={fields.message.errorId}
									errors={fields.message.errors}
								/>
							</div>
						</div>
						<StatusButton
							className="mt-8 w-full"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
						>
							Demo aanvragen
						</StatusButton>
					</Form>
				)}
			</div>

			<p className="mt-8 text-center text-xs text-text-dim">
				Geen verplichtingen. We gebruiken je gegevens alleen om contact
				op te nemen.{' '}
				<Link to="/privacy" className="underline hover:text-white">
					Privacyverklaring
				</Link>
			</p>
		</div>
	)
}
